import { IUserActivityModel } from "./IUserActivityModel";
import { IUserActivityModelStep } from "./IUserActivityModelStep";
import { IUserActivityViewModel } from "./IUserActivityViewModel";
import { IUserActivityViewModelStep } from "./IUserActivityViewModelStep";
import { IUserProfile } from "../../@types/Wyanoke/UserProfile"

export class UserActivityModel implements IUserActivityModel {
  public readonly useractivity: IUserActivityModel["useractivity"];

  constructor(userProfile: IUserProfile) {
    const contactid = userProfile.contactid || "";
    const activityid = window.Activity ? window.Activity.Id || "" : "";
    const url = window.Activity ? window.Activity.Url || "" : "";
    const title = window.Activity ? window.Activity.Title || "" : "";
    const expires = window.Activity ? window.Activity.Expires || "" : "";
    const firstviewed = (new Date(Date.now())).toString();
    const lastviewed = window.Activity ? window.Activity.LastViewed || "" : "";
    const providers = window.Activity ? window.Activity.Providers || [] : [];
    const specialties = window.Activity
      ? window.Activity.Specialties || []
      : [];
    const topics = window.Activity ? window.Activity.Topics || [] : [];
    const collections = window.Activity
      ? window.Activity.Collections || []
      : [];
    const lmsname = window.Activity ? window.Activity.LMSName || "" : "";
    const isExternal = window.Activity ? window.Activity.isExternal || false : false;

    this.useractivity = {
      activity: {
        activityid,
        collections: [...collections],
        expires,
        providers: providers
                  .filter(Boolean)
                  .map((p : any)=>({providerid: p.Id, title: p.Title})),
        specialties: [...specialties],
        title,
        topics: [...topics],
        url,
        lmsname
      },
      disabled: false,
      firstviewed,
      lastviewed,
      steps: [],
      user: {
        address: {},
        contactid
      },
      isExternal: isExternal
    };
  }

  public static transformToViewModel(
    model: IUserActivityModel | null
  ): IUserActivityViewModel {
    const userActivity = model ? model.useractivity : null;
    const rawSteps = userActivity ? userActivity.steps : null;
    const sitecoreSteps = window.Activity ? window.Activity.Steps || [] : [];

    const rawCertificate = userActivity ? userActivity.certificate : null;
    const rawCredits = rawCertificate ? rawCertificate.credits : null;
    const rawType = rawCertificate ? rawCertificate.credittype : null;
    const rawDate = rawCertificate
      ? rawCertificate.date === undefined
        ? null
        : rawCertificate.date
      : null;

    const claimedCredits = rawCredits ? parseFloat(rawCredits.toString()) : 0;

    const certificate: IUserActivityViewModel["certificate"] =
      typeof rawCredits === "number" && typeof rawType === "string"
        ? {
            claimedCredits,
            credits: rawCredits,
            credittype: rawType,
            date: rawDate
          }
        : null;

    const steps: IUserActivityViewModelStep[] = sitecoreSteps.map(
      (sitecoreStep : any) => {
        const stepid = sitecoreStep ? sitecoreStep.Id : null;
        const type = sitecoreStep ? sitecoreStep.Type : null;
        if (!stepid || !type) {
          console.error("Invalid activity step data", sitecoreStep);
          throw new Error("Invalid activity step data");
        }

        const url = sitecoreStep.Url || null;
        const rawStep = rawSteps
          ? rawSteps.find(s => s.stepid === stepid)
          : null;

        const complete = rawStep && rawStep.complete ? rawStep.complete : false;
        const timestamp = (complete ? (Date.now.toString()) : null);

        return { stepid, complete, timestamp, type, url };
      }
    );

    const attestation = model?.useractivity?.attestation ? model.useractivity.attestation : false;

    return { certificate, steps, attestation };
  }

  public static updateFromContext(
    model: IUserActivityModel,
    userProfile: IUserProfile
  ): void {
    const body = model.useractivity || {};
    model.useractivity = body;

    UserActivityModel.updateActivity(body);
    UserActivityModel.updateSteps(body);
    UserActivityModel.updateUser(body, userProfile);
  }

  private static updateActivity(
    server: IUserActivityModel["useractivity"]
  ): void {
    const sitecoreActivity = window.Activity || {};
    const id = sitecoreActivity.Id;

    if (id) {
      const serverActivity = server.activity || { id };
      server.activity = serverActivity;
      server.activity.activityid = id;

      if (sitecoreActivity.Title) {
        serverActivity.title = sitecoreActivity.Title;
      }

      serverActivity.specialties = sitecoreActivity.Specialties
        ? [...sitecoreActivity.Specialties]
        : [];

      serverActivity.providers = sitecoreActivity.Providers
        ? sitecoreActivity.Providers
          .filter(Boolean)
          .map((p : any )=>({
            providerid: p.Id,
            title: p.Title
          }))
        : [] as {providerid?: string; title?: string}[];

      serverActivity.topics = sitecoreActivity.Topics
        ? [...sitecoreActivity.Topics]
        : [];

      serverActivity.collections = sitecoreActivity.Collections
        ? [...sitecoreActivity.Collections]
        : [];

      serverActivity.url = sitecoreActivity.Url ? sitecoreActivity.Url : "";
      serverActivity.expires = sitecoreActivity.Expires
        ? sitecoreActivity.Expires
        : "";
    }
  }

  private static updateSteps(server: IUserActivityModel["useractivity"]): void {
    const sitecoreActivity = window.Activity || {};

    if (sitecoreActivity.Steps) {
      const existingSteps = server.steps || [];
      server.steps = sitecoreActivity.Steps.map((sitecoreStep : any) => {
        const id = sitecoreStep.Id;

        if (!id) {
          return;
        }

        const serverStep: IUserActivityModelStep = existingSteps.find(
          e => e.stepid === id
        ) || { stepid: id };

        if (sitecoreStep.Title) {
          serverStep.title = sitecoreStep.Title;
        }

        if (sitecoreStep.Type) {
          serverStep.type = sitecoreStep.Type;
        }

        return serverStep;
      }).filter(Boolean) as IUserActivityModelStep[];
    }
  }

  private static updateUser(
    server: IUserActivityModel["useractivity"],
    sitecoreUser: IUserProfile
  ): void {
    const contactid = sitecoreUser.contactid;

    if (contactid) {
      const serverUser = server.user || { contactid };
      server.user = serverUser;
      serverUser.contactid = contactid;

      if (sitecoreUser.email) {
        serverUser.email = sitecoreUser.email;
      }

      if (sitecoreUser.firstname) {
        serverUser.firstname = sitecoreUser.firstname;
      }

      if (sitecoreUser.lastname) {
        serverUser.lastname = sitecoreUser.lastname;
      }

      if (sitecoreUser.npi) {
        serverUser.npi = sitecoreUser.npi;
      }

      if (sitecoreUser.profession) {
        serverUser.profession = sitecoreUser.profession;
      }

      if (sitecoreUser.specialty) {
        serverUser.specialty = sitecoreUser.specialty;
      }

      if (sitecoreUser.subprofession) {
        serverUser.subprofession = sitecoreUser.subprofession;
      }

      if (sitecoreUser.subspecialty) {
        serverUser.subspecialty = sitecoreUser.subspecialty;
      }
    }
  }
}
